import React, { useEffect, useState } from 'react';
import Router, { useRouter } from 'next/router';
import { toast } from 'sonner';
import Modal from '@/components/ui/scout/modal/Modal';
import Upload from '@/components/ui/scout/upload/Upload';
import Location from '@/components/ui/scout/Location';
import { Views } from '@/types/view';
import { FilesWithLocations } from '@/types/location';
import { FiltersState } from '@/types/scout';
import { filterLocations } from '@/utils/scout';
import Map from '@/components/ui/scout/map/Map';
import * as Toast from '@/components/ui/toast/Toast';
import { useScout } from '@/hooks/useScout';
import List from '@/components/ui/scout/list/List';
import { deregisterLocations } from '@/api/location';
import ConfirmationModal from '@/components/ui/shared/ConfirmationModal';
import Header from '@/components/ui/scout/header/Header';
import BurstModal from '@/components/ui/scout/burst/BurstModal';
import { useUser } from '@/hooks/useUser';

export const BURST_PRICE = 125;

interface Props {
  page: Views;
}

export default function Scout({ page }: Props) {
  const router = useRouter();
  const {
    query: { id, search },
  } = router;

  const { user } = useUser();
  const { locations } = useScout();
  const urlLocationId = Array.isArray(id) ? id[1] : null;

  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [burstModalOptions, setBurstModalOptions] = useState<{
    isOpen: boolean;
    locationsIds: string[];
    successCallback: () => void;
  }>({
    isOpen: false,
    locationsIds: [],
    successCallback: () => {},
  });
  const [filesWithLocations, setFilesWithLocations] =
    useState<FilesWithLocations>({});
  const [activeView, setActiveView] = useState(page);

  const [filters, setFilters] = useState<FiltersState>({
    description: '',
    search: (search as string) ?? '',
  });

  const [activeLocationId, setActiveLocationId] = useState<
    string | number | null
  >(activeView === Views.Location ? urlLocationId : null);

  const activeLocation = locations.find(
    location => location._id === activeLocationId,
  );

  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);

  const {
    selectedLocationIds,
    setLocations,
    setLastSelectedView,
    setActiveSequenceIndex,
    setActiveFrameIndex,
  } = useScout();

  const filteredLocations = filterLocations(locations, filters);

  const selectionCallback = (
    locationId: string | number,
    lastSelectedView: Views,
    index = 0,
  ) => {
    setActiveLocationId(locationId);
    setLastSelectedView(lastSelectedView);
    setActiveView(Views.Location);
    setActiveSequenceIndex(index);
    setActiveFrameIndex({ value: 0 });

    Router.push(`/scout/location/${locationId}`, undefined, { shallow: true });
  };

  const removeLocations = async (ids: string[]) => {
    const response = await deregisterLocations(ids, user?.currentOrganization);

    if ('error' in response) {
      console.error(response.error);
      toast.custom(t => (
        <Toast.Default
          title="Try Again"
          description="We were unable to remove the locations, please try again."
          buttons={[
            {
              label: 'Dismiss',
              onClick: () => toast.dismiss(t),
              variant: 'secondary',
              size: 'fw',
            },
          ]}
        />
      ));
      return;
    }

    toast.custom(t => (
      <Toast.Default
        title="Locations Removed"
        description="The locations has been successfully removed."
        buttons={[
          {
            label: 'Dismiss',
            onClick: () => {
              toast.dismiss(t);
            },
            variant: 'secondary',
            size: 'fw',
          },
        ]}
      />
    ));

    setLocations(prevState => prevState.filter(loc => !ids.includes(loc._id)));
  };

  useEffect(() => {
    const shouldStripUrl =
      (urlLocationId && !activeLocation) ||
      (urlLocationId && activeView !== Views.Location);

    const shouldAddUrl =
      activeLocation && !urlLocationId && activeView === Views.Location;

    if (shouldStripUrl && activeView !== Views.List) {
      Router.push(activeView, undefined, { shallow: true });
    } else if (shouldAddUrl) {
      Router.replace(`/scout/location/${activeLocation._id}`, undefined, {
        shallow: true,
      });
    }
  }, [urlLocationId, activeLocation, activeView]);

  useEffect(() => {
    const handlePopState = (event: PopStateEvent) => {
      if (event.state.as.includes('/scout/location')) {
        if (event.state.as === '/scout/location') {
          setActiveLocationId(null);
        } else {
          const id = event.state.as.split('/scout/location/').pop();
          setActiveLocationId(id);
        }

        setActiveView(Views.Location);
      }

      if (event.state.as === '/scout/map') {
        setActiveView(Views.Map);
      }

      if (event.state.as.includes('/scout/list')) {
        setActiveView(Views.List);
      }
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  return (
    <>
      <ConfirmationModal
        title="Remove Location"
        description="Are you sure you want to remove these locations? This will impact all members of your organization."
        warning={`You have selected ${selectedLocationIds.length} locations`}
        action="Remove"
        open={isConfirmationModalOpen}
        setOpen={setIsConfirmationModalOpen}
        onClick={() => removeLocations(selectedLocationIds)}
      />
      <BurstModal
        open={burstModalOptions.isOpen}
        locationsIds={burstModalOptions.locationsIds}
        successCallback={burstModalOptions.successCallback}
        setBurstModalOptions={setBurstModalOptions}
        burstAmount={burstModalOptions.locationsIds.length * BURST_PRICE}
      />
      <Modal
        showModal={isUploadModalOpen}
        setShowModal={setIsUploadModalOpen}
        hideCloseButton>
        <Upload
          filesWithLocations={filesWithLocations}
          setFilesWithLocations={setFilesWithLocations}
          setShowModal={setIsUploadModalOpen}
          setActiveView={setActiveView}
        />
      </Modal>
      <div className="relative">
        <Header
          page={page}
          activeView={activeView}
          setActiveView={setActiveView}
          filters={filters}
          setFilters={setFilters}
          locations={locations}
          activeLocation={activeLocation}
          setIsUploadModalOpen={setIsUploadModalOpen}
          setIsConfirmationModalOpen={setIsConfirmationModalOpen}
          setBurstModalOptions={setBurstModalOptions}
          setActiveLocationId={setActiveLocationId}
        />
        <div
          data-active={activeView === Views.List}
          className="flex data-[active=false]:hidden">
          <List
            locations={filteredLocations}
            selectionCallback={selectionCallback}
            filters={filters}
            isActiveView={activeView === Views.List}
          />
        </div>
        <div
          data-active={activeView === Views.Map}
          className="flex data-[active=false]:hidden">
          <Map
            activeView={activeView}
            locations={filteredLocations}
            selectionCallback={selectionCallback}
          />
        </div>
        <div
          data-active={activeView === Views.Location}
          className="flex flex-col data-[active=false]:hidden">
          <Location
            activeLocation={activeLocation}
            activeView={activeView}
            setActiveLocationId={setActiveLocationId}
            locations={filteredLocations}
            itemsPerPage={10}
            selectionCallback={selectionCallback}
            setActiveView={setActiveView}
            setBurstModalOptions={setBurstModalOptions}
          />
        </div>
      </div>
    </>
  );
}
